import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import promotionService, {
  PromotionDiscountType,
  PromotionScope,
  CreatePromotionRuleDTO,
  DAY_BITS,
} from '../../services/promotionService';
import { productService } from '../../services/productService';
import PromotionScopePicker, { ScopeOption } from './PromotionScopePicker';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './PromotionFormPage.css';
import { ArrowLeft, Save } from 'lucide-react';

const MAX_LENGTHS = {
  name: 100,
  description: 300,
};

/** Day keys in the backend's bit order — Mon(bit 0) .. Sun(bit 6). */
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const NUMERIC_FIELDS = ['discountValue', 'minOrderAmount', 'maxDiscountAmount', 'priority'];

interface PromotionForm {
  name: string;
  description: string;
  activeDays: number;
  timeFrom: string;
  timeTo: string;
  validFrom: string;
  validTo: string;
  discountType: PromotionDiscountType;
  discountValue: number | '';
  scope: PromotionScope;
  categoryIds: number[];
  productIds: number[];
  minOrderAmount: number | '';
  maxDiscountAmount: number | '';
  priority: number | '';
  isActive: boolean;
}

interface FieldErrors {
  name?: string;
  description?: string;
  discountValue?: string;
  minOrderAmount?: string;
  maxDiscountAmount?: string;
  priority?: string;
  timeTo?: string;
  validTo?: string;
  scope?: string;
}

/** Date inputs are date-only; widen them to cover the whole intended first/last day in UTC. */
const toUtcStart = (date: string) => (date ? new Date(`${date}T00:00:00Z`).toISOString() : null);
const toUtcEnd = (date: string) => (date ? new Date(`${date}T23:59:59Z`).toISOString() : null);
/** A time input yields "HH:mm"; the backend TimeSpan expects "HH:mm:ss". */
const toTimeSpan = (time: string) => (time ? `${time}:00` : null);

const mapFormToDTO = (form: PromotionForm): CreatePromotionRuleDTO => ({
  name: form.name.trim(),
  description: form.description || undefined,
  activeDays: form.activeDays,
  timeFrom: toTimeSpan(form.timeFrom),
  timeTo: toTimeSpan(form.timeTo),
  validFrom: toUtcStart(form.validFrom),
  validTo: toUtcEnd(form.validTo),
  discountType: form.discountType,
  discountValue: Number(form.discountValue),
  scope: form.scope,
  categoryIds: form.scope === PromotionScope.Categories ? form.categoryIds : [],
  productIds: form.scope === PromotionScope.Products ? form.productIds : [],
  minOrderAmount: form.minOrderAmount === '' ? undefined : Number(form.minOrderAmount),
  maxDiscountAmount: form.maxDiscountAmount === '' ? undefined : Number(form.maxDiscountAmount),
  priority: form.priority === '' ? 0 : Number(form.priority),
  isActive: form.isActive,
});

const PromotionCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [categories, setCategories] = useState<ScopeOption[]>([]);
  const [products, setProducts] = useState<ScopeOption[]>([]);
  const [formData, setFormData] = useState<PromotionForm>({
    name: '',
    description: '',
    activeDays: 0,
    timeFrom: '',
    timeTo: '',
    validFrom: '',
    validTo: '',
    discountType: PromotionDiscountType.Percentage,
    discountValue: '',
    scope: PromotionScope.AllProducts,
    categoryIds: [],
    productIds: [],
    minOrderAmount: '',
    maxDiscountAmount: '',
    priority: 0,
    isActive: true,
  });

  const isPercentage = formData.discountType === PromotionDiscountType.Percentage;

  const loadScopeOptions = useCallback(async () => {
    try {
      const [categoryResponse, productResponse] = await Promise.all([
        productService.getCategories({ pageNumber: 1, pageSize: 200 }),
        productService.getProducts({ pageNumber: 1, pageSize: 500 }),
      ]);
      setCategories(
        (categoryResponse.data.data ?? []).map((category) => ({ id: category.id, label: category.name }))
      );
      setProducts(
        (productResponse.data.data ?? []).map((product) => ({
          id: product.id,
          label: `${product.sku} — ${product.name}`,
        }))
      );
    } catch (err) {
      setError(getServerErrorMessage(err) || t('promotion.failedToLoadScopeOptions'));
    }
  }, [t]);

  useEffect(() => {
    loadScopeOptions();
  }, [loadScopeOptions]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';

    let finalValue: any;
    if (isCheckbox) {
      finalValue = (e.target as HTMLInputElement).checked;
    } else if (name === 'discountType' || name === 'scope') {
      finalValue = Number(value);
    } else if (NUMERIC_FIELDS.includes(name)) {
      finalValue = value === '' ? '' : Number(value);
    } else {
      finalValue = value;
      if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
        finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    if (!isCheckbox) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const toggleDay = (bit: number) => {
    setFormData((prev) => ({ ...prev, activeDays: prev.activeDays ^ bit }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = t('promotion.formValidation.nameRequired');
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('promotion.formValidation.nameMaxLength', { max: MAX_LENGTHS.name });
      isValid = false;
    }

    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('promotion.formValidation.descriptionMaxLength', { max: MAX_LENGTHS.description });
      isValid = false;
    }

    if (formData.discountValue === '' || formData.discountValue === null) {
      errors.discountValue = t('promotion.formValidation.discountRequired');
      isValid = false;
    } else if (Number(formData.discountValue) <= 0) {
      errors.discountValue = t('promotion.formValidation.discountPositive');
      isValid = false;
    } else if (isPercentage && Number(formData.discountValue) > 100) {
      errors.discountValue = t('promotion.formValidation.discountPercentMax');
      isValid = false;
    }

    if (formData.minOrderAmount !== '' && Number(formData.minOrderAmount) < 0) {
      errors.minOrderAmount = t('promotion.formValidation.minOrderNegative');
      isValid = false;
    }

    if (formData.maxDiscountAmount !== '' && Number(formData.maxDiscountAmount) < 0) {
      errors.maxDiscountAmount = t('promotion.formValidation.maxDiscountNegative');
      isValid = false;
    }

    if (formData.priority !== '' && Number(formData.priority) < 0) {
      errors.priority = t('promotion.formValidation.priorityNegative');
      isValid = false;
    }

    // A time window only makes sense with both ends supplied.
    if (Boolean(formData.timeFrom) !== Boolean(formData.timeTo)) {
      errors.timeTo = t('promotion.formValidation.timeRangeIncomplete');
      isValid = false;
    } else if (formData.timeFrom && formData.timeTo && formData.timeTo <= formData.timeFrom) {
      errors.timeTo = t('promotion.formValidation.timeToBeforeFrom');
      isValid = false;
    }

    if (formData.validFrom && formData.validTo && formData.validTo < formData.validFrom) {
      errors.validTo = t('promotion.formValidation.validToBeforeFrom');
      isValid = false;
    }

    if (formData.scope === PromotionScope.Categories && formData.categoryIds.length === 0) {
      errors.scope = t('promotion.formValidation.categoriesRequired');
      isValid = false;
    }

    if (formData.scope === PromotionScope.Products && formData.productIds.length === 0) {
      errors.scope = t('promotion.formValidation.productsRequired');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);
    try {
      await promotionService.create(mapFormToDTO(formData));
      navigate('/sales/promotions', { state: { message: t('promotion.createSuccess') } });
    } catch (err) {
      setError(getServerErrorMessage(err) || t('promotion.failedToCreate'));
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="edit-container promotion-form-page">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/sales/promotions')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('promotion.newPromotion')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/sales/promotions')}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading
                ? <><span className="ims-btn-spinner" />{t('common.saving')}</>
                : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('promotion.createNewPromotion')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label className="form-label">
                      {t('promotion.name')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.name}
                      placeholder={t('promotion.namePlaceholder')}
                    />
                    {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                    {!fieldErrors.name && <small className="form-helper">{t('promotion.nameHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('promotion.description')}
                      <span className="char-counter">{formData.description.length}/{MAX_LENGTHS.description}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.description ? 'is-invalid' : ''}`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.description}
                      placeholder={t('promotion.description')}
                      rows={3}
                    />
                    {fieldErrors.description && <small className="form-error">{fieldErrors.description}</small>}
                    {!fieldErrors.description && <small className="form-helper">{t('promotion.descriptionHint')}</small>}
                  </div>

                  <div className="promotion-form-row">
                    <div className="form-group">
                      <label className="form-label">
                        {t('promotion.discountType')} <span className="required">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="discountType"
                        value={formData.discountType}
                        onChange={handleInputChange}
                      >
                        <option value={PromotionDiscountType.Percentage}>{t('promotion.percentage')}</option>
                        <option value={PromotionDiscountType.FlatAmount}>{t('promotion.flatAmount')}</option>
                      </select>
                      <small className="form-helper">{t('promotion.discountTypeHint')}</small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('promotion.discountValue')} <span className="required">*</span>
                      </label>
                      <div className="promotion-input-with-suffix">
                        <input
                          type="number"
                          className={`form-input ${fieldErrors.discountValue ? 'is-invalid' : ''}`}
                          name="discountValue"
                          value={formData.discountValue}
                          onChange={handleInputChange}
                          placeholder="0"
                          step="0.01"
                          min="0"
                        />
                        <span className="promotion-input-suffix">{isPercentage ? '%' : t('promotion.amount')}</span>
                      </div>
                      {fieldErrors.discountValue && <small className="form-error">{fieldErrors.discountValue}</small>}
                      {!fieldErrors.discountValue && (
                        <small className="form-helper">
                          {isPercentage ? t('promotion.discountValuePercentHint') : t('promotion.discountValueFlatHint')}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('promotion.activeDays')}</label>
                    <div className="promotion-day-picker">
                      {DAY_KEYS.map((key, index) => (
                        <label key={key} className="promotion-day-chip">
                          <input
                            type="checkbox"
                            checked={(formData.activeDays & DAY_BITS[index]) !== 0}
                            onChange={() => toggleDay(DAY_BITS[index])}
                          />
                          <span>{t(`promotion.dayShort.${key}`)}</span>
                        </label>
                      ))}
                    </div>
                    <small className="form-helper">{t('promotion.activeDaysHint')}</small>
                  </div>

                  <div className="promotion-form-row">
                    <div className="form-group">
                      <label className="form-label">{t('promotion.timeFrom')}</label>
                      <input
                        type="time"
                        className="form-input"
                        name="timeFrom"
                        value={formData.timeFrom}
                        onChange={handleInputChange}
                      />
                      <small className="form-helper">{t('promotion.timeWindowHint')}</small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('promotion.timeTo')}</label>
                      <input
                        type="time"
                        className={`form-input ${fieldErrors.timeTo ? 'is-invalid' : ''}`}
                        name="timeTo"
                        value={formData.timeTo}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.timeTo && <small className="form-error">{fieldErrors.timeTo}</small>}
                      {!fieldErrors.timeTo && <small className="form-helper">{t('promotion.timeToHint')}</small>}
                    </div>
                  </div>

                  <div className="promotion-form-row">
                    <div className="form-group">
                      <label className="form-label">{t('promotion.validFrom')}</label>
                      <input
                        type="date"
                        className="form-input"
                        name="validFrom"
                        value={formData.validFrom}
                        onChange={handleInputChange}
                      />
                      <small className="form-helper">{t('promotion.validFromHint')}</small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('promotion.validTo')}</label>
                      <input
                        type="date"
                        className={`form-input ${fieldErrors.validTo ? 'is-invalid' : ''}`}
                        name="validTo"
                        value={formData.validTo}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.validTo && <small className="form-error">{fieldErrors.validTo}</small>}
                      {!fieldErrors.validTo && <small className="form-helper">{t('promotion.validToHint')}</small>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('promotion.scope')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="scope"
                      value={formData.scope}
                      onChange={handleInputChange}
                    >
                      <option value={PromotionScope.AllProducts}>{t('promotion.scopeAllProducts')}</option>
                      <option value={PromotionScope.Categories}>{t('promotion.scopeCategories')}</option>
                      <option value={PromotionScope.Products}>{t('promotion.scopeProducts')}</option>
                    </select>
                    {fieldErrors.scope && <small className="form-error">{fieldErrors.scope}</small>}
                    {!fieldErrors.scope && <small className="form-helper">{t('promotion.scopeHint')}</small>}
                  </div>

                  {formData.scope === PromotionScope.Categories && (
                    <div className="form-group">
                      <label className="form-label">{t('promotion.categories')}</label>
                      <PromotionScopePicker
                        options={categories}
                        selectedIds={formData.categoryIds}
                        onChange={(ids) => {
                          setFormData((prev) => ({ ...prev, categoryIds: ids }));
                          setFieldErrors((prev) => ({ ...prev, scope: undefined }));
                        }}
                        emptyMessage={t('promotion.noCategoriesFound')}
                        invalid={Boolean(fieldErrors.scope)}
                      />
                    </div>
                  )}

                  {formData.scope === PromotionScope.Products && (
                    <div className="form-group">
                      <label className="form-label">{t('promotion.products')}</label>
                      <PromotionScopePicker
                        options={products}
                        selectedIds={formData.productIds}
                        onChange={(ids) => {
                          setFormData((prev) => ({ ...prev, productIds: ids }));
                          setFieldErrors((prev) => ({ ...prev, scope: undefined }));
                        }}
                        emptyMessage={t('promotion.noProductsFound')}
                        invalid={Boolean(fieldErrors.scope)}
                      />
                    </div>
                  )}

                  <div className="promotion-form-row">
                    <div className="form-group">
                      <label className="form-label">{t('promotion.minOrderAmount')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.minOrderAmount ? 'is-invalid' : ''}`}
                        name="minOrderAmount"
                        value={formData.minOrderAmount}
                        onChange={handleInputChange}
                        placeholder={t('promotion.noMinimum')}
                        step="0.01"
                        min="0"
                      />
                      {fieldErrors.minOrderAmount && <small className="form-error">{fieldErrors.minOrderAmount}</small>}
                      {!fieldErrors.minOrderAmount && <small className="form-helper">{t('promotion.minOrderAmountHint')}</small>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('promotion.maxDiscountAmount')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.maxDiscountAmount ? 'is-invalid' : ''}`}
                        name="maxDiscountAmount"
                        value={formData.maxDiscountAmount}
                        onChange={handleInputChange}
                        placeholder={t('promotion.noCap')}
                        step="0.01"
                        min="0"
                        disabled={!isPercentage}
                      />
                      {fieldErrors.maxDiscountAmount && <small className="form-error">{fieldErrors.maxDiscountAmount}</small>}
                      {!fieldErrors.maxDiscountAmount && (
                        <small className="form-helper">
                          {isPercentage ? t('promotion.maxDiscountAmountHint') : t('promotion.maxDiscountOnlyPercentage')}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('promotion.priority')}</label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.priority ? 'is-invalid' : ''}`}
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      step="1"
                      min="0"
                    />
                    {fieldErrors.priority && <small className="form-error">{fieldErrors.priority}</small>}
                    {!fieldErrors.priority && <small className="form-helper">{t('promotion.priorityHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span>{t('promotion.isActive')}</span>
                    </label>
                    <small className="form-helper">{t('promotion.isActiveHint')}</small>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>{t('promotion.activeDays')}</strong>
                  <p>{t('promotion.help.activeDays')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('promotion.scope')}</strong>
                  <p>{t('promotion.help.scope')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('promotion.priority')}</strong>
                  <p>{t('promotion.help.priority')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('promotion.maxDiscountAmount')}</strong>
                  <p>{t('promotion.help.maxDiscountAmount')}</p>
                </div>
                <div className="help-item">
                  <strong>{t('promotion.isActive')}</strong>
                  <p>{t('promotion.help.isActive')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PromotionCreatePage;
